import R from "ramda";
import { doubleEncode } from "utils/double-encode";
import { http } from "api/http";
import { filtersToUrlParams } from "api/filters-to-url-params";

export const fetchDocuments = (accountId, filterData, pagination) => (
  http.get(`/accounts/${accountId}/documents?${filtersToUrlParams(filterData, pagination)}`)
    .then(response => {
      const documents = R.path(["data", "documents"], response);
      const totalCountFiltered = R.path(["data", "totalCountFiltered"], response);
      const totalCount = R.path(["data", "totalCount"], response);
      const totalSumFiltered = R.path(["data", "totalSumFiltered"], response);

      return { documents, response, totalCount, totalCountFiltered, totalSumFiltered };
    })
);

export const batchUpdateDocuments = (accountId, documents, formData) => {
  const vendor = R.path(["vendor", "name"], formData);
  const documentType = R.path(["documentType", "name"], formData);
  const currency = R.path(["currency", "name"], formData);
  const name = R.path(["name"], formData);

  if (vendor === undefined && documentType === undefined && currency === undefined && name === undefined) {
    return Promise.resolve(null);
  }

  const ajaxPromises = documents.map(document => {
    const data = updateDocumentValues(document, vendor, documentType, currency, name);
    return http.put(`/accounts/${accountId}/documents/${document.id}`, data);
  });
  return Promise.all(ajaxPromises);
};

const updateDocumentValues = (document, vendor, documentType, currency, name) => {
  const data = R.clone(document);
  if (vendor) {
    data.vendor = vendor;
  }
  if (currency) {
    data.currency = currency;
  }
  if (name) {
    data.name = name;
  }
  if (documentType) {
    if (data.name !== undefined) {
      data.vendor = data.name;
    }
    if (data.vendor !== undefined) {
      data.name = data.vendor;
    }
    if (documentType === "receipt") {
      data.currency = data.currency ? data.currency : "AUD";
      data.categories = data.categories ? data.categories : [];
      data.total = data.total ? data.total : 0;
      data.tax = data.tax ? data.tax : 0;
    }
    data.type = documentType.toLowerCase();
  }
  return data;
};

export const batchAssignCategories = (accountId, documents, selectedCategories) => {
  const ajaxPromises = documents.map(document => {
    const data = R.clone(document);
    if (selectedCategories.length === 0 || data.categories === undefined) {
      data.categories = [];
    }
    data.categories = R.uniq(R.concat(data.categories, selectedCategories));
    return http.put(`/accounts/${accountId}/documents/${document.id}`, data);
  });
  return Promise.all(ajaxPromises);
};

export const putDocument = (accountId, documentId, data) => {
  const document = JSON.stringify(data);
  return http.put(`/accounts/${accountId}/documents/${documentId}`, document);
};

export const batchDownloadDocuments = (exportType, accountId, documentIds, emails) => (
  http.post(`/accounts/${accountId}/exports`, {
    documents: documentIds,
    emails,
    exportType
  })
);

export const batchChangeViewedSetting = (accountId, documents, isViewed) => {
  const ajaxPromises = documents.map(document => {
    const newDocument = R.clone(document);
    newDocument.viewed = isViewed;
    return http.put(`/accounts/${accountId}/documents/${document.id}`, newDocument);
  });
  return Promise.all(ajaxPromises);
};

export const mergeDocuments = (accountId, formData) => http.post(`/accounts/${accountId}/documents`, formData);

export const trashStatusMultipleDocuments = (accountId, documents, trashStatus) => {
  const ajaxPromises = documents.map(document => {
    const newDocument = R.clone(document);
    newDocument.trashed = trashStatus;
    return http.put(`/accounts/${accountId}/documents/${document.id}`, newDocument);
  });
  return Promise.all(ajaxPromises);
};

export const uploadDocuments = (accountId, formData) => http.upload(`accounts/${accountId}/documents`, formData);

export const deleteDocumentsPermanently = (accountId, documents) => {
  const ajaxPromises = documents.map(document => http.delete(`/accounts/${accountId}/documents/${document.id}`));
  return Promise.all(ajaxPromises);
};

export const getShareUrl = (accountId, documentId) => (
  http.post(`/accounts/${accountId}/share-document/${documentId}`)
    .then(response => {
      const shareUrl = R.path(["data", "url"], response);
      return { shareUrl };
    })
);

export const fetchVendorRules = (accountId, documents) => {
  const ajaxPromises = documents.map(document => {
    if (document.vendor === undefined || document.vendor === null || document.vendor === "") {
      return null;
    }
    return http.get(`accounts/${accountId}/vendors/${doubleEncode(document.vendor)}/rules`);
  });
  return Promise.all(ajaxPromises)
    .then(response => {
      const rules = response.map(rule => R.path(["data", "rules"], rule));
      return { rules, documents };
    });
};

export const addIncludeRule = (accountId, categoryId, rule) =>
  http.post(`accounts/${accountId}/categories/${categoryId}/rules/vendors`, rule);

export const deleteIncludeRule = (accountId, categoryId, vendorId) =>
  http.delete(`/accounts/${accountId}/categories/${categoryId}/rules/vendors/${vendorId}`);

export const exportSelected = (accountId, documentIds) => (
  http.post(`/accounts/${accountId}/exports`, {
    documents: documentIds,
    exportType: "accounting"
  })
);

export const exportAll = (accountId) => {
  const queryString = "processing_state=PROCESSED&type=receipt&not_category=Exported&not_category=Do+not+export";
  const exportsObject = {
    exportType: "accounting",
    filter: queryString
  };
  return http.post(`/accounts/${accountId}/exports`, exportsObject);
};

export const selectAllDocuments = (accountId, filters) => {
  const pagination = {
    limit: 0,
    pageNumber: 0
  };
  return http.get(`/accounts/${accountId}/documents?${filtersToUrlParams(filters, pagination)}`)
    .then(response => {
      const documents = R.path(["data", "documents"], response);
      const documentIds = R.pluck("id")(documents);
      return { documentIds, documents };
    });
};

export const mergeVendors = (accountId, documents, newVendor) => {
  const ajaxPromises = documents.map(document => {
    const data = R.merge(document, { vendor: newVendor, name: newVendor });
    return http.put(`/accounts/${accountId}/documents/${document.id}`, data);
  });
  return Promise.all(ajaxPromises);
};



// WEBPACK FOOTER //
// ./src/js/api/documents.js